import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPopper, Placement } from '@popperjs/core';
import { useQuery, gql } from '@apollo/client';
import './style.css';
import { useTranslation } from 'react-i18next';

interface IGeneratedMaterial {
  materialId: number;

  percentage: number;
  netWeight: number;
}

interface ILine {
  id: number;
  materialId: number;
  producedMaterials: IGeneratedMaterial[];
  netWeight?: number;

  material: { name: string; id: string };
}

interface MaterialPopoverProps {
  line: ILine;
  placement?: Placement;
  onUpdateProducedMaterials: (newMaterials: IGeneratedMaterial[]) => void;
  canEdit: boolean;
}

const GET_MATERIALS = gql`
  query GetMaterialsForProduction($where: String!) {
    getMaterials(where: $where) {
      data {
        id
        name
      }
    }
  }
`;

const MaterialPopover: React.FC<MaterialPopoverProps> = ({
  line,
  placement = 'auto',
  onUpdateProducedMaterials,
  canEdit,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [materials, setMaterials] = useState<IGeneratedMaterial[]>(line.producedMaterials || []);
  const { t } = useTranslation();

  const [errors, setErrors] = useState<
    Record<number, Partial<Record<keyof IGeneratedMaterial, string>>>
  >({});

  const maxLineWeight = line.netWeight ?? 0;
  const { data: materialData, loading: materialsLoading } = useQuery(GET_MATERIALS, {
    variables: { where: JSON.stringify({ useInProduction: true }) },
  });

  useEffect(() => {
    setMaterials(structuredClone(line.producedMaterials || []));
  }, [line.producedMaterials]);
  const materialOptions = materialData?.getMaterials?.data || [];
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const popperInstanceRef = useRef<any>(null);
  const [isPopoverReady, setIsPopoverReady] = useState(false);

  // Auto-validate all rows when materials change
  useEffect(() => {
    const newErrors: typeof errors = {};
    let totalWeight = 0;

    materials.forEach((mat, idx) => {
      const errs: Partial<Record<keyof IGeneratedMaterial, string>> = {};

      if (!mat.materialId || mat.materialId === 0) errs.materialId = 'Απαιτείται';

      if (mat.netWeight <= 0) errs.netWeight = 'Πρέπει να είναι > 0';

      totalWeight += mat.netWeight;

      if (Object.keys(errs).length > 0) newErrors[idx] = errs;
    });

    if (totalWeight > maxLineWeight) {
      materials.forEach((_, idx) => {
        newErrors[idx] = {
          ...newErrors[idx],
          netWeight: 'Το συνολικό βάρος ξεπερνά το όριο',
        };
      });
    }

    setErrors(newErrors);
  }, [materials, maxLineWeight]);

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const recalculatePercentages = useCallback(
    (updated: IGeneratedMaterial[]) =>
      updated.map((mat) => ({
        ...mat,
        percentage: maxLineWeight ? (mat.netWeight / maxLineWeight) * 100 : 0,
      })),
    [maxLineWeight],
  );

  const handleToggle = () => setShowPopover((prev) => !prev);

  const handleChange = (index: number, field: keyof IGeneratedMaterial, value: string | number) => {
    if (!canEdit) return;
    const newValue = Number(value);

    const updated = structuredClone(materials);

    // Check for duplicate materialId
    if (field === 'materialId') {
      const duplicateIndex = updated.findIndex(
        (mat, i) => mat.materialId === newValue && i !== index,
      );

      if (duplicateIndex !== -1) {
        setErrors((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            materialId: 'Το υλικό έχει ήδη επιλεγεί',
          },
        }));
        return;
      } else {
        // Clear error if user fixed it
        setErrors((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            materialId: undefined,
          },
        }));
      }
    }

    updated[index][field] = newValue;

    // Apply weight limit if necessary
    if (field === 'netWeight') {
      const currentTotal = materials.reduce(
        (sum, mat, i) => sum + (i === index ? 0 : mat.netWeight),
        0,
      );
      updated[index].netWeight = Math.min(newValue, maxLineWeight - currentTotal);
    }

    setMaterials(recalculatePercentages(updated));
  };

  const handleAddMaterial = () => {
    if (!canEdit) return;
    setMaterials((prev) => [...prev, { materialId: 0, percentage: 0, netWeight: 0 }]);
  };

  const handleRemoveMaterial = (index: number) => {
    if (!canEdit) return;

    const updated = structuredClone(materials);
    updated.splice(index, 1); // Remove the row
    setMaterials(recalculatePercentages(updated));
  };
  const handleSave = () => {
    if (hasErrors || !canEdit) return;
    onUpdateProducedMaterials(materials);
    setShowPopover(false);
  };
  // Outside click handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !popoverRef.current?.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setShowPopover(false);
        //resetLocalState();
        setMaterials(line.producedMaterials || []);
      }
    };
    if (showPopover) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  // Popper positioning
  useEffect(() => {
    if (showPopover && buttonRef.current && popoverRef.current) {
      const timeout = setTimeout(() => {
        popperInstanceRef.current = createPopper(buttonRef.current, popoverRef.current, {
          placement,
          modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { boundary: 'viewport' } },
            { name: 'computeStyles', options: { adaptive: false } },
          ],
        });
        setIsPopoverReady(true);
      }, 0);
      return () => clearTimeout(timeout);
    } else {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
      setIsPopoverReady(false);
    }
  }, [showPopover, placement]);

  return (
    <div className="popover-container d-flex justify-content-center">
      <button ref={buttonRef} onClick={handleToggle} className="btn-trigger" type="button">
        {canEdit
          ? materials.length > 0
            ? `Παραγόμενα Υλικά ${materials.length}`
            : 'Προσθήκη Υλικών'
          : 'Προβολή Υλικών'}
      </button>

      {showPopover && (
        <div ref={popoverRef} className="outerPopover">
          {isPopoverReady && (
            <div className={`innerPopover ${showPopover ? 'enter' : ''}`}>
              <h4 className="popoverTitle">
                {t('Υλικά από {{weight}} kg {{name}}', {
                  weight: line.netWeight ?? 0,
                  name: line.material?.name,
                })}
              </h4>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{t('Υλικό')}</th>
                    <th>{t('Βάρος (kg)')}</th>
                    <th>{t('Ποσοστό (%)')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((mat, idx) => (
                    <tr key={idx}>
                      <td>
                        <select
                          value={mat.materialId}
                          onChange={(e) => handleChange(idx, 'materialId', e.target.value)}
                          className={`form-control form-control-sm ${
                            errors[idx]?.materialId ? 'is-invalid' : ''
                          }`}
                          disabled={!canEdit || materialsLoading}
                        >
                          <option value="0">{t('Επιλέξτε Υλικό')}</option>
                          {materialOptions.map((opt) => {
                            const isAlreadySelected = materials.some(
                              (m, i) => m.materialId === opt.id && i !== idx,
                            );

                            return (
                              <option key={opt.id} value={opt.id} disabled={isAlreadySelected}>
                                {opt.name} {isAlreadySelected ? '🔒 (Ήδη επιλεγμένο)' : ''}
                              </option>
                            );
                          })}
                        </select>
                        {errors[idx]?.materialId && (
                          <div className="invalid-feedback">{errors[idx]?.materialId}</div>
                        )}
                      </td>

                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm ${
                            errors[idx]?.netWeight ? 'is-invalid' : ''
                          }`}
                          value={mat.netWeight}
                          onChange={(e) => handleChange(idx, 'netWeight', e.target.value)}
                          disabled={!canEdit}
                          min={0}
                        />
                        {errors[idx]?.netWeight && (
                          <div className="invalid-feedback">{errors[idx]?.netWeight}</div>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={mat.percentage.toFixed(2)}
                          disabled
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveMaterial(idx)}
                          disabled={!canEdit}
                        >
                          X
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {canEdit && (
                <div className="d-flex justify-content-end mt-2">
                  <button
                    onClick={handleAddMaterial}
                    className="btn btn-primary btn-sm"
                    type="button"
                  >
                    Προσθήκη Υλικών
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-success btn-sm ms-2"
                    type="button"
                    disabled={hasErrors}
                  >
                    {t('Αποθήκευση')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MaterialPopover;

